import { DetailedInsight } from '@api/api/insights';
import { KgInsight } from '@api/api/kg-insights';
import { SentimentSummaryCard } from '@terminal/components/sentiment-summary-card/SentimentSummaryCard';
import { useInsightsById } from '@terminal/react-query-hooks/insights/useInsight';
import { useKgInsightsById } from '@terminal/react-query-hooks/insights/useKgInsight';
import { useMonthlySentimentState } from '@terminal/react-query-hooks/themes/useMonthlySentimentState';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import {
  PulseMonthlySummaries,
  PulseSentimentDirection,
  PulseSubject,
} from '@terminal/views/theme-overview/tab-content/pulse-overview/pulse-types';
import { citationIdsByTypeForDirection } from '@terminal/views/theme-overview/tab-content/pulse-overview/utils/citationIdsForDirection';
import styles from './MonthlySentimentCards.module.scss';

const DIRECTIONS = ['tailwind', 'headwind'] as const;

export const MonthlySentimentCards = ({
  summaries,
  subject,
  themeId,
  source = POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW,
  getCardTitle,
  onOpenDrawer,
}: {
  summaries?: PulseMonthlySummaries;
  subject: PulseSubject;
  themeId?: string;
  source?: PosthogTrackingSource;
  getCardTitle?: (direction: PulseSentimentDirection) => string;
  onOpenDrawer: (direction: PulseSentimentDirection) => void;
}) => {
  const { kgInsightIds, scenarioInsightIds } = citationIdsByTypeForDirection(
    summaries,
    'all'
  );

  const kgInsightsById = useKgInsightsById(kgInsightIds);
  const scenarioInsightsById = useInsightsById(scenarioInsightIds);
  const insightsById = new Map<string, KgInsight | DetailedInsight>([
    ...kgInsightsById,
    ...scenarioInsightsById,
  ]);
  const scenarioInsightIdSet = new Set(scenarioInsightIds);
  const tailwindState = useMonthlySentimentState({
    summary: summaries?.tailwind,
    subject,
    direction: 'tailwind',
  });
  const headwindState = useMonthlySentimentState({
    summary: summaries?.headwind,
    subject,
    direction: 'headwind',
  });
  const states = { tailwind: tailwindState, headwind: headwindState };

  return (
    <div className={styles.root}>
      {DIRECTIONS.map(direction => {
        const summary = summaries?.[direction];

        return (
          <SentimentSummaryCard
            key={direction}
            direction={direction}
            title={getCardTitle?.(direction)}
            summary={summary}
            themeId={themeId}
            insightsById={insightsById}
            scenarioInsightIds={scenarioInsightIdSet}
            source={source}
            state={states[direction]}
            onOpen={() => onOpenDrawer(direction)}
          />
        );
      })}
    </div>
  );
};
